import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Button,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import Autocomplete from "@mui/material/Autocomplete";

import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
countries.registerLocale(enLocale);

const API_BASE_URL = "https://api.hr-ebnhaian.cloud/api/v1";

const nationalityOptions = Object.values(
  countries.getNames("en", { select: "official" })
);

const JOB_ROLES = [
  "CALL_CENTER",
  "RECEPTIONIST",
  "HR",
  "ACCOUNTANT",
  "INSURANCE",
  "MARKETING",
  "OFFICE_BOY",
  "MANAGER",
];

const Section = ({ title, children }) => (
  <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
    <Typography fontWeight="bold">{title}</Typography>
    <Divider sx={{ my: 2 }} />
    {children}
  </Paper>
);

const AddEmployee = () => {
  const [centers, setCenters] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/centers`).then((res) => {
      setCenters(res.data?.data || []);
    });
  }, []);

  const REQUIRED_FIELDS = [
    "firstName",
    "lastName",
    "phone",
    "jobRole",
    "jobTitle",
    "center",
    "startDate",
    "basicSalary",
  ];

  const isRequired = (name) => REQUIRED_FIELDS.includes(name);

  const fieldError = (name) =>
    Boolean(formik.touched[name] && formik.errors[name]);

  const helper = (name) => (formik.touched[name] ? formik.errors[name] : "");

  const formik = useFormik({
    initialValues: {
      /* BASIC */
      firstName: "",
      fatherName: "",
      lastName: "",
      gender: "",
      birthDate: "",
      nationality: "",
      maritalStatus: "",
      childrenCount: 0,

      /* CONTACT */
      phone: "",
      emergencyPhone: "",
      email: "",
      address: "",

      /* JOB */
      jobRole: "",
      jobTitle: "",
      center: "",
      startDate: "",
      endDate: "",
      status: "ACTIVE",

      /* HR */
      probationEndDate: "",
      noticePeriodDays: "",

      /* SALARY */
      basicSalary: "",
      salaryType: "FIXED",
      workingDaysPerMonth: 30,
      workingHoursPerDay: 8,
      housing: 0,
      transport: 0,
      other: 0,
      overtimeEnabled: false,
      overtimeRate: 0,
      latePenaltyPerHour: 0,
      deductionOther: 0,
      paymentMethod: "BANK_TRANSFER",

      /* DOCUMENTS */
      passportNumber: "",
      passportIssueDate: "",
      passportExpiryDate: "",
      passportFile: null,

      residenceNumber: "",
      residenceIssueDate: "",
      residenceExpiryDate: "",
      residenceFile: null,

      visaNumber: "",
      visaIssueDate: "",
      visaExpiryDate: "",
      visaFile: null,

      /* LEAVE */
      annualLeave: 30,
      sickLeave: 0,
      unpaidLeave: 0,

      /* SYSTEM */
      notes: "",
      profileImage: null,
    },

    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      phone: Yup.string().required("Phone is required"),
      jobRole: Yup.string().required("Job role is required"),
      jobTitle: Yup.string().required("Job title is required"),
      center: Yup.string().required("Center is required"),
      startDate: Yup.string().required("Start date is required"),
      basicSalary: Yup.number()
        .typeError("Basic salary must be a number")
        .required("Basic salary is required"),

      // ✅ الملفات ليست مطلوبة — لا يوجد required عليها
      passportFile: Yup.mixed().nullable(),
      residenceFile: Yup.mixed().nullable(),
      visaFile: Yup.mixed().nullable(),
      profileImage: Yup.mixed().nullable(),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      try {
        const fd = new FormData();
        Object.entries(values).forEach(([k, v]) => {
          // ✅ لا تبعت ملفات فاضية ولا قيم فاضية
          if (v === null) return;
          if (typeof v === "string" && v.trim() === "") return;
          fd.append(k, v);
        });

        await axios.post(`${API_BASE_URL}/employees`, fd);
        alert("Employee added successfully");
      } catch (err) {
        alert(err.response?.data?.message || "Error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const submitAll = async () => {
    // ✅ يخلي كل الحقول المطلوبة "touched" ليظهر الخطأ مباشرة
    const touched = {};
    REQUIRED_FIELDS.forEach((k) => (touched[k] = true));
    formik.setTouched({ ...formik.touched, ...touched }, true);

    // بعد setTouched، نفّذ submit
    formik.handleSubmit();
  };

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Add Employee
      </Typography>

      <Paper sx={{ mb: 3, p: 2, borderRadius: 3 }}>
        <Typography fontWeight="bold">
          All fields in one page — required fields marked with *
        </Typography>
      </Paper>

      <form onSubmit={formik.handleSubmit}>
        {/* PERSONAL */}
        <Section title="Personal & Contact Information">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Avatar
                src={preview}
                sx={{ width: 120, height: 120, border: "3px solid #1976d2" }}
              />
              <Button component="label" size="small">
                Upload Image
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) {
                      setPreview(URL.createObjectURL(f));
                      formik.setFieldValue("profileImage", f);
                    }
                  }}
                />
              </Button>
            </Grid>

            {[
              ["firstName", "First Name"],
              ["fatherName", "Father Name"],
              ["lastName", "Last Name"],
            ].map(([k, l]) => (
              <Grid item xs={12} md={4} key={k}>
                <TextField
                  label={l}
                  fullWidth
                  required={isRequired(k)}
                  {...formik.getFieldProps(k)}
                  error={fieldError(k)}
                  helperText={helper(k)}
                />
              </Grid>
            ))}

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Gender"
                fullWidth
                {...formik.getFieldProps("gender")}
              >
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Birth Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...formik.getFieldProps("birthDate")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                options={nationalityOptions}
                value={formik.values.nationality || null}
                onChange={(e, value) =>
                  formik.setFieldValue("nationality", value || "")
                }
                onBlur={() => formik.setFieldTouched("nationality", true)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nationality"
                    fullWidth
                    error={fieldError("nationality")}
                    helperText={helper("nationality")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="caption">
                  Phone {isRequired("phone") ? "*" : ""}
                </Typography>
                <PhoneInput
                  country="ae"
                  value={formik.values.phone}
                  onChange={(v) => formik.setFieldValue("phone", v)}
                  onBlur={() => formik.setFieldTouched("phone", true)}
                  inputStyle={{ width: "100%" }}
                />
                {fieldError("phone") && (
                  <Typography variant="caption" color="error">
                    {helper("phone")}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption">Emergency Phone</Typography>
              <PhoneInput
                country="ae"
                value={formik.values.emergencyPhone}
                onChange={(v) => formik.setFieldValue("emergencyPhone", v)}
                inputStyle={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                {...formik.getFieldProps("address")}
              />
            </Grid>
          </Grid>
        </Section>

        {/* JOB */}
        <Section title="Job & HR Information">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Job Role"
                fullWidth
                required={isRequired("jobRole")}
                {...formik.getFieldProps("jobRole")}
                error={fieldError("jobRole")}
                helperText={helper("jobRole")}
              >
                {JOB_ROLES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Job Title"
                fullWidth
                required={isRequired("jobTitle")}
                {...formik.getFieldProps("jobTitle")}
                error={fieldError("jobTitle")}
                helperText={helper("jobTitle")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Center"
                fullWidth
                required={isRequired("center")}
                {...formik.getFieldProps("center")}
                error={fieldError("center")}
                helperText={helper("center")}
              >
                {centers.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Start Date"
                fullWidth
                required={isRequired("startDate")}
                InputLabelProps={{ shrink: true }}
                {...formik.getFieldProps("startDate")}
                error={fieldError("startDate")}
                helperText={helper("startDate")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="End Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...formik.getFieldProps("endDate")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Status"
                fullWidth
                {...formik.getFieldProps("status")}
              >
                {["ACTIVE", "ON_LEAVE", "INACTIVE", "TERMINATED"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Probation End Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...formik.getFieldProps("probationEndDate")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                label="Notice Period Days"
                fullWidth
                {...formik.getFieldProps("noticePeriodDays")}
              />
            </Grid>
          </Grid>
        </Section>

        {/* SALARY */}
        <Section title="Salary & Payroll">
          <Grid container spacing={2}>
            {[
              ["basicSalary", "Basic Salary"],
              ["housing", "Housing"],
              ["transport", "Transport"],
              ["other", "Other Allowance"],
              ["overtimeRate", "Overtime Rate / Hour"],
              ["latePenaltyPerHour", "Late Penalty / Hour"],
              ["deductionOther", "Other Deduction"],
            ].map(([k, l]) => (
              <Grid item xs={12} md={4} key={k}>
                <TextField
                  type="number"
                  label={l}
                  fullWidth
                  required={isRequired(k)}
                  {...formik.getFieldProps(k)}
                  error={fieldError(k)}
                  helperText={helper(k)}
                />
              </Grid>
            ))}

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.overtimeEnabled}
                    onChange={(e) =>
                      formik.setFieldValue("overtimeEnabled", e.target.checked)
                    }
                  />
                }
                label="Overtime Enabled"
              />
            </Grid>
          </Grid>
        </Section>

        {/* DOCUMENTS */}
        {/* DOCUMENTS */}
        <Section title="Documents (Optional)">
          {[
            ["passport", "Passport", "passportFile"],
            ["visa", "Visa", "visaFile"],
            ["residence", "Emirates ID", "residenceFile"],
          ].map(([key, label, fileField]) => (
            <Box key={key} mb={3}>
              <Typography fontWeight="bold" mb={1}>
                {label}
              </Typography>

              <Grid container spacing={2}>
                {/* Number / Issue / Expiry */}
                {[
                  { field: "Number", label: "Number" },
                  { field: "IssueDate", label: "Issue Date" },
                  { field: "ExpiryDate", label: "Expiry Date" },
                ].map((f, i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <TextField
                      label={`${label} ${f.label}`}
                      type={f.label.includes("Date") ? "date" : "text"}
                      InputLabelProps={
                        f.label.includes("Date") ? { shrink: true } : {}
                      }
                      fullWidth
                      {...formik.getFieldProps(`${key}${f.field}`)}
                    />
                  </Grid>
                ))}

                {/* Upload + File Preview */}
                <Grid item xs={12} md={4}>
                  <Button component="label" variant="outlined">
                    Upload {label} (Optional)
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        formik.setFieldValue(fileField, file);
                      }}
                    />
                  </Button>

                  {/* 👇 إذا في ملف */}
                  {formik.values[fileField] && (
                    <Box mt={1}>
                      <Chip
                        label={
                          typeof formik.values[fileField] === "string"
                            ? formik.values[fileField] // ملف قديم من السيرفر
                            : formik.values[fileField].name // ملف جديد
                        }
                        color="success"
                        onDelete={() =>
                          formik.setFieldValue(fileField, null)
                        }
                        deleteIcon={<CloseIcon />}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}
        </Section>
        {/* LEAVE */}
        <Section title="Leave Balance & Notes">
          <Grid container spacing={2}>
            {[
              ["annualLeave", "Annual Leave"],
              ["sickLeave", "Sick Leave"],
              ["unpaidLeave", "Unpaid Leave"],
            ].map(([k, l]) => (
              <Grid item xs={12} md={4} key={k}>
                <TextField
                  type="number"
                  label={l}
                  fullWidth
                  {...formik.getFieldProps(k)}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                {...formik.getFieldProps("notes")}
              />
            </Grid>
          </Grid>
        </Section>

        {/* SAVE */}
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            color="success"
            size="large"
            type="button"
            onClick={submitAll}
            disabled={formik.isSubmitting}
          >
            Save Employee
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddEmployee;